import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import moment from 'moment';
import history from '../../../history';
import {
  fetchSafetyAuditRepeatableById,
  fetchSafetyAuditScanOnDemandById,
  createSafetyAudit,
  createSafetyAuditRepeatable,
  updateSafetyAuditRepeatable,
  deleteSafetyAuditRepeatable,
  createSafetyAuditScanOnDemand,
  updateSafetyAuditScanOnDemand,
  deleteSafetyAuditScanOnDemand,
  fetchSafetyAuditTemplates,
  fetchCompanyEquipments,
  fetchSafetyAuditTemplateById
} from '../../../api/v4';
import {
  FREQUENCY_OPTIONS,
  MONTHLY_FREQUENCY_OPTIONS,
  YEARLY_FREQUENCY_OPTIONS
} from '../../../constants/constants';
import {
  getActiveCompany,
  getActiveLocationId,
  getActiveProject
} from '../../../selectors/company';
import { getLoggedInUser } from '../../../selectors/users';
import { isFutureDate, notFutureDate } from '../../../utils/formValidation';
import {
  getSelectedGroups,
  getSelectedProjects
} from '../../../utils/employeeDGPHelper';
import customToast from '../../../utils/customToast';
import Card from '../../../components/Card';
import { IReportDatePicker } from '../../../components/inputs/DateTimePicker';
import DayOfWeekButtons from '../../../components/inputs/DayOfWeekButtons';
import DeleteItemModal from '../../../components/Modal/deleteItemModal';
import DueBy from '../../../components/inputs/DueBy';
import HeaderAndFooter from '../../../components/HeaderAndFooter';
import Header from '../../../components/Header';
import HierarchySelector from '../../../components/HierarchySelector';
import RadioButtons from '../../../components/inputs/RadioButtons';
import { SaveCancelFooter } from '../../../components/Footer';
import SaveChangesModal from '../../../components/Modal/saveChangesModal';
import {
  Textbox,
  Checkbox,
  Dropdown,
  EmployeeDropdown
} from '../../../components/inputs';
import MissingRequiredModal from '../../../components/Modal/missingRequiredModal';

export default function SafetyAuditCreate() {
  const company = useSelector(getActiveCompany);
  const group = useSelector(getActiveLocationId);
  const project = useSelector(getActiveProject);
  const activeUser = useSelector(getLoggedInUser);

  const [audit, setAudit] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templateDropdown, setTemplateDropdown] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupDeleted, setGroupDeleted] = useState(null);
  const [missingRequired, setMissingRequired] = useState(false);
  const [missingModalOpen, setMissingModalOpen] = useState(false);
  const [equipments, setEquipments] = useState([]);
  const [equipmentDropdown, setEquipmentDropdown] = useState([]);
  const [eGroupDropdown, setEGroupDropdown] = useState(null);
  const [eProjectDropdown, setEProjectDropdown] = useState(null);

  const { safetyAuditId, type } = useParams();
  const location = useLocation();

  const { favAreas, favGroups, favTemplate, fromScreen } =
    location?.state || {};

  const auditType = location.pathname.includes('safety')
    ? 'safety'
    : location.pathname.includes('quality')
      ? 'quality'
      : 'loto';
  const auditTypeName =
    auditType === 'loto'
      ? 'Lockout/Tagout Procedure'
      : `${auditType.charAt(0).toUpperCase() + auditType.slice(1)} Audit`;
  const createOrPerform = favTemplate ? 'Perform' : 'Create';

  const fromEquipment = location?.state?.audit?.ownerType === 'equipment';

  const equipmentGroupDropdown = location?.state?.equipmentGroupDropdown;

  const equipmentProjectDropdown = location?.state?.equipmentProjectDropdown;

  useEffect(() => {
    fetchCompanyEquipments().then(equipRes => {
      setEquipments(
        favAreas?.length
          ? equipRes?.filter(e =>
              e?.projectIds?.some(p => favAreas.includes(p))
            )
          : favGroups?.length
            ? equipRes?.filter(e =>
                e?.groupIds?.some(g => favGroups.includes(g))
              )
            : equipRes
      );
    });
    const passedState = history.location.state;

    if (!favGroups) setEGroupDropdown(passedState?.equipmentGroupDropdown);
    if (!favAreas) setEProjectDropdown(passedState?.equipmentProjectDropdown);
    if (favTemplate) return;
    if (safetyAuditId && type) {
      type === 'repeatable'
        ? fetchSafetyAuditRepeatableById(safetyAuditId).then(response => {
            setGroupDeleted(
              !company.groups.find(g => g._id === response.groupId)
            );
            setAudit({ ...response, schedule: 'repeating' });
          })
        : fetchSafetyAuditScanOnDemandById(safetyAuditId).then(response =>
            setAudit({ ...response, schedule: 'scan' })
          );
      setIsEditing(true);
    } else {
      setAudit(
        passedState
          ? { ...passedState.audit }
          : {
              groupId: group?._id,
              projectId: project?._id
            }
      );
      setIsCreating(true);
    }

    fetchSafetyAuditTemplates(auditType).then(response => {
      response = response.filter(template => !template.isArchived);
      response = passedState
        ? response.filter(r =>
            passedState?.linkedAuditTemplates.includes(r._id)
          )
        : response;
      setTemplates(response);
      setTemplateDropdown(
        response.map(template => {
          return { label: template.title, value: template._id };
        })
      );
    });
  }, [
    fromEquipment,
    group,
    project,
    safetyAuditId,
    auditType,
    type,
    company,
    favTemplate,
    favGroups,
    favAreas
  ]);

  useEffect(() => {
    if (!favTemplate) return;
    setIsCreating(true);
    fetchSafetyAuditTemplateById(favTemplate?.value).then(tplR => {
      setTemplateDropdown([{ label: tplR.title, value: tplR._id }]);
      const availableEquipment = equipments?.filter(
        e =>
          e?.linkedSafetyAuditTemplates?.some(aud => aud._id === tplR._id) ||
          e?.linkedLOTOTemplates?.some(aud => aud._id === tplR._id)
      );
      setEquipmentDropdown(availableEquipment);
      delete tplR._id;
      delete tplR.auditLog;
      tplR.sections.forEach(s => {
        delete s._id;
        s.questions.forEach(q => delete q._id);
      });

      setAudit({
        schedule: 'immediate',
        groupId:
          favGroups?.length === 1 ? favGroups[0] : !!group ? group._id : null,
        projectId:
          favAreas?.length === 1 ? favAreas[0] : !!project ? project._id : null,
        ...tplR,
        template: favTemplate?.value,
        assignedTo: [activeUser._id],
        hasAreasToSelect:
          !!favAreas?.length ||
          !!company?.groups
            ?.filter(g => favGroups?.includes(g._id) || group._id === g._id)
            ?.flatMap(g => g?.projects?.filter(p => !p?.isHidden))?.length
      });
    });
  }, [
    favTemplate,
    favGroups,
    favAreas,
    equipments,
    activeUser._id,
    company,
    group._id,
    group,
    project
  ]);

  const handleTemplateSelection = value => {
    const template = { ...templates.find(temp => temp._id === value) };
    delete template._id;
    delete template.auditLog;
    template.sections.forEach(s => {
      delete s._id;
      s.questions.forEach(q => {
        delete q._id;
      });
    });

    const availableEquipments = equipments?.filter(
      e =>
        e?.linkedSafetyAuditTemplates?.some(sa => sa._id === value) ||
        e?.linkedLOTOTemplates?.some(sa => sa._id === value)
    );

    setEquipmentDropdown(availableEquipments);
    const nullEquipmentInfo = !availableEquipments?.some(
      e => e._id === audit.linkedEquipment
    );

    if (nullEquipmentInfo) {
      setEGroupDropdown(null);
      setEProjectDropdown(null);
    }

    setAudit({
      ...audit,
      ...template,
      template: value,
      linkedEquipment: nullEquipmentInfo ? null : audit.linkedEquipment,
      ownerType: nullEquipmentInfo ? '' : audit?.ownerType,
      ownerId: nullEquipmentInfo ? '' : audit?.ownerId,
      type: audit._id ? `${template.type}Audits` : template.type
    });
    setHasChanges(true);
  };

  const handleBack = () =>
    fromScreen
      ? history.push(fromScreen)
      : fromEquipment
        ? history.push(`/app/equipmentContainer/${audit.ownerId}`)
        : history.push(`/app/audits/${auditType}`);

  const submit = () => {
    if (audit.repeatingOn) {
      audit.repeatingOn = Array.isArray(audit?.repeatingOn)
        ? audit?.repeatingOn
        : [audit?.repeatingOn];
    }

    audit.companyId = company._id;
    audit.projectId = audit.projectId ? audit.projectId : null;
    if (audit?._id) {
      if (audit.schedule === 'repeating') {
        updateSafetyAuditRepeatable(audit).then(handleBack);
      } else {
        updateSafetyAuditScanOnDemand(audit).then(handleBack);
      }
    } else {
      if (audit.schedule === 'immediate') {
        createSafetyAudit({
          ...audit,
          dueDate: favTemplate
            ? moment().add(5, 'hours').toDate()
            : audit?.dueDate
        }).then(r => {
          if (favTemplate) {
            history.push({
              pathname: `/app/audits/${auditType}/perform/${r._id}`,
              state: { prevLocation: location?.pathname }
            });
          } else {
            customToast('Successfully Created', 'success');
            handleBack();
          }
        });
      } else if (audit.schedule === 'repeating') {
        createSafetyAuditRepeatable({
          ...audit,
          type: `${auditType}Audits`
        }).then(handleBack);
      } else {
        delete audit.groupId;
        delete audit.projectId;
        createSafetyAuditScanOnDemand({
          ...audit,
          type: `${auditType}Audits`
        }).then(handleBack);
      }
    }
  };

  const handleDelete = () => {
    if (audit.schedule === 'repeating') {
      deleteSafetyAuditRepeatable(audit).then(handleBack);
    } else {
      deleteSafetyAuditScanOnDemand(audit).then(handleBack);
    }
  };

  const handleEquipmentSelection = value => {
    let equipment = equipments?.find(e => e._id === value);
    setAudit({
      ...audit,
      linkedEquipment: value,
      ownerType: value ? 'equipment' : '',
      ownerId: value ?? '',
      schedule: audit.schedule === 'scan' ? null : audit.schedule,
      groupId: !!group
        ? group._id
        : favGroups?.length === 1
          ? favGroups[0]
          : null,
      projectId: !!project ? project._id : null
    });
    if (equipment) {
      const groupDD = getSelectedGroups(
        [company],
        favGroups?.length
          ? equipment?.groupIds?.filter(g => favGroups?.includes(g))
          : equipment.groupIds
      );
      setEGroupDropdown(groupDD?.map(g => ({ value: g._id, label: g.name })));

      if (equipment?.projectIds?.length) {
        const areaDD = getSelectedProjects(
          groupDD,
          favAreas?.length
            ? equipment?.projectIds?.filter(p => favAreas?.includes(p))
            : equipment?.projectIds
        );
        setEProjectDropdown([
          ...areaDD?.map(a => ({
            value: a._id,
            label: a.name,
            groupId: a.groupId
          }))
        ]);
      } else setEProjectDropdown(null);
    } else {
      setEGroupDropdown(null);
      setEProjectDropdown(null);
    }
  };

  const canSubmit =
    ((audit?.groupId && audit?.hasAreasToSelect
      ? audit?.projectId
      : audit?.groupId) ||
      audit.schedule === 'scan') &&
    ((audit?.schedule === 'immediate' &&
      (!fromEquipment || audit?.linkedEquipment) &&
      ((audit?.dueDate && notFutureDate(audit?.dueDate)) || favTemplate) &&
      audit?.assignedTo?.length > 0 &&
      audit?.sections?.length > 0) ||
      (audit?.schedule === 'repeating' &&
        audit?.frequency &&
        audit?.dueBy &&
        audit?.dueByTime &&
        (audit?.frequency === 'quarterly' ||
          audit?.frequency === 'daily' ||
          audit?.repeatingOn?.length > 0) &&
        audit?.assignedTo?.length > 0 &&
        audit?.sections?.length > 0 &&
        audit?.template) ||
      (audit?.schedule === 'scan' &&
        (audit?.sections?.title || audit?.sections?.length > 0)));

  if (isEditing) {
    const template = templates?.find(t => t.title === audit.title);
    audit.template = template?._id;
  }

  const header = (
    <Header
      title={`${auditTypeName}`}
      section={
        isCreating
          ? `${createOrPerform} New ${auditType === 'loto' ? 'Procedure' : 'Audit'}`
          : `Edit ${auditType === 'loto' ? 'Procedure' : 'Audit'}`
      }
      clickBack={() =>
        hasChanges ? setShowSaveChangesModal(true) : handleBack()
      }
      rightButtons={{
        text: `Delete ${auditTypeName}`,
        onClick: () => setShowDeleteModal(true),
        color: 'red',
        visible: isEditing
      }}
    />
  );

  const footer = (
    <SaveCancelFooter
      editing={isEditing}
      cancelButtonClick={() =>
        isEditing && hasChanges
          ? setShowSaveChangesModal(true)
          : isEditing && !hasChanges
            ? history.push(`/app/audits/${auditType}`)
            : setShowSaveChangesModal(true)
      }
      saveButtonClick={() => (canSubmit ? submit() : setMissingModalOpen(true))}
      onMouseEnter={() => setMissingRequired(true)}
      saveButtonText={createOrPerform}
    />
  );

  const template = templates.find(r => r.title === audit.title);

  return (
    <HeaderAndFooter
      Header={header}
      Footer={footer}
      showFooter={isCreating || isEditing}
    >
      <Card>
        {!favTemplate && (
          <RadioButtons
            fieldLabel="Schedule"
            options={[
              { value: 'immediate', label: 'Immediately' },
              { value: 'repeating', label: 'Repeating' }
            ].concat(
              !fromEquipment ? [{ value: 'scan', label: 'Scan on Demand' }] : []
            )}
            isRequired
            onChange={values =>
              setAudit({
                ...audit,
                schedule: values,
                linkedEquipment:
                  values === 'scan' ? null : audit.linkedEquipment,
                frequency: '',
                groupId: values === 'scan' ? null : audit.groupId,
                projectId: values === 'scan' ? null : audit.projectId
              })
            }
            currentValue={audit?.schedule}
            touched={missingRequired && !audit?.schedule}
            disabled={!isCreating}
          />
        )}
        <Dropdown
          options={templateDropdown}
          fieldLabel={`${auditTypeName} Template`}
          currentValue={audit?.template || template}
          isRequired
          onChange={value => handleTemplateSelection(value)}
          placeholder={`Select a ${auditTypeName}`}
          touched={missingRequired && !audit?.template}
          searchable
          disabled={favTemplate}
        />
        {auditType !== 'quality' &&
          equipmentDropdown?.length > 0 &&
          audit?.schedule !== 'scan' && (
            <Dropdown
              options={equipmentDropdown?.map(e => ({
                value: e._id,
                label: e.title
              }))}
              fieldLabel="Equipment"
              currentValue={audit?.linkedEquipment}
              onChange={handleEquipmentSelection}
              placeholder={
                !audit?.template
                  ? 'Select a Template first'
                  : !equipmentDropdown?.length
                    ? 'No available Equipment'
                    : 'Select an Equipment'
              }
              searchable
              allowUnset
              disabled={!audit?.template || fromEquipment}
              isRequired={fromEquipment}
              touched={
                fromEquipment && missingRequired && !audit?.linkedEquipment
              }
              alphabetize
            />
          )}

        {audit.schedule !== 'scan' ? (
          <HierarchySelector
            onGroupChange={values => {
              const selectedGroup = company?.groups?.find(
                g => g._id === values.value
              );
              const equipmentAreasForGroup = eProjectDropdown?.filter(
                p => p.groupId === values.value
              );
              const hasAreasToSelect = audit?.linkedEquipment
                ? !!equipmentAreasForGroup?.length
                : !!selectedGroup?.projects?.filter(p => !p?.isHidden)?.length;
              const projectToSet =
                favAreas?.length === 1
                  ? selectedGroup?.projects?.find(p => p._id === favAreas[0])
                      ?._id || null
                  : null;
              setAudit({
                ...audit,
                groupId: values?.value,
                projectId: projectToSet,
                hasAreasToSelect
              });
            }}
            groupId={audit?.groupId}
            groupDisabled={
              (!groupDeleted &&
                (!isCreating ||
                  (fromEquipment && equipmentGroupDropdown?.length === 1))) ||
              favGroups?.length === 1
            }
            onProjectChange={values =>
              setAudit({ ...audit, projectId: values })
            }
            projectId={audit?.projectId}
            projectDisabled={
              (!groupDeleted &&
                (!audit?.groupId ||
                  !isCreating ||
                  (fromEquipment &&
                    equipmentProjectDropdown?.length < 2 &&
                    equipmentGroupDropdown?.length < 2))) ||
              favAreas?.length === 1
            }
            filteredGroupDropdown={eGroupDropdown}
            filteredProjectDropdown={
              audit?.hasAreasToSelect &&
              eProjectDropdown?.filter(p => p.groupId === audit?.groupId)
            }
            groupTouched={missingRequired && !audit?.groupId}
            projectTouched={
              audit?.hasAreasToSelect && missingRequired && !audit?.projectId
            }
            filterForGroups={favGroups?.length > 0 ? favGroups : null}
            filterForAreas={favAreas?.length > 0 ? favAreas : null}
          />
        ) : (
          <></>
        )}
        <Textbox
          currentValue={audit?.jobLocation}
          fieldLabel="Specific Location"
          onChange={e => setAudit({ ...audit, jobLocation: e })}
          type="textarea"
          placeholder={`Briefly describe where this ${auditTypeName} ${favTemplate ? 'will' : 'should'} be done.`}
        />
        {audit?.schedule === 'repeating' && (
          <Dropdown
            options={FREQUENCY_OPTIONS}
            fieldLabel="Frequency:"
            currentValue={audit?.frequency}
            onChange={value =>
              setAudit({ ...audit, frequency: value, repeatingOn: undefined })
            }
            isRequired
            touched={missingRequired && !audit?.frequency}
          />
        )}
        {audit?.frequency === 'weekly' && (
          <DayOfWeekButtons
            fieldLabel="Assign Every:"
            disabled={audit?.schedule !== 'repeating'}
            onChange={values => setAudit({ ...audit, repeatingOn: values })}
            currentValue={audit?.repeatingOn}
            touched={missingRequired && !audit?.repeatingOn}
          />
        )}
        {audit?.frequency === 'monthly' && (
          <Dropdown
            options={MONTHLY_FREQUENCY_OPTIONS}
            fieldLabel="Assign Every:"
            currentValue={audit?.repeatingOn}
            onChange={values => setAudit({ ...audit, repeatingOn: values })}
            isRequired
            touched={missingRequired && !audit?.repeatingOn}
          />
        )}
        {audit?.frequency === 'yearly' && (
          <Dropdown
            options={YEARLY_FREQUENCY_OPTIONS}
            fieldLabel="Assign Every:"
            currentValue={audit?.repeatingOn}
            onChange={values => setAudit({ ...audit, repeatingOn: values })}
            isRequired
            touched={missingRequired && !audit?.repeatingOn}
          />
        )}
        {audit?.schedule === 'repeating' ? (
          <DueBy
            isRequired
            dueByType={audit?.dueBy}
            dueTime={audit?.dueByTime}
            dueByNumber={audit?.dueByNumber}
            onDueByTypeChange={v => setAudit({ ...audit, dueBy: v })}
            onDueByNumberChange={v => setAudit({ ...audit, dueByNumber: v })}
            onDueTimeChange={v => setAudit({ ...audit, dueByTime: v })}
            touched={missingRequired}
          />
        ) : audit?.schedule === 'immediate' && !favTemplate ? (
          <IReportDatePicker
            fieldLabel="Due By:"
            isRequired
            onChange={value => setAudit({ ...audit, dueDate: value })}
            pickTime
            currentValue={audit?.dueDate}
            name="createAudit"
            touched={isFutureDate(audit?.dueDate) && missingRequired}
            errorMessage={audit?.dueDate ? isFutureDate(audit?.dueDate) : null}
            minDate={new Date()}
          />
        ) : (
          ''
        )}
        {audit?.schedule !== 'scan' && !favTemplate && (
          <EmployeeDropdown
            multi
            selectButtons
            permissions={[100, 400, 500, 900]}
            fieldLabel="Assignee(s):"
            currentValue={favTemplate ? [activeUser?._id] : audit?.assignedTo}
            isRequired
            onChange={values => setAudit({ ...audit, assignedTo: values })}
            searchable
            name="multiEmployees"
            group={audit.groupId}
            touched={missingRequired && !audit?.assignedTo}
          />
        )}
        {audit?.schedule !== 'scan' && !favTemplate && (
          <Checkbox
            fieldLabel="Assign Individually"
            name="isIndividual"
            onChange={value => setAudit({ ...audit, isIndividual: value })}
            currentValue={audit?.isIndividual}
          />
        )}
        {audit?.schedule === 'repeating' && !isCreating && (
          <Checkbox
            fieldLabel={`Pause ${auditTypeName}`}
            name="pauseAudit"
            onChange={value => setAudit({ ...audit, isPaused: value })}
            currentValue={audit?.isPaused}
          />
        )}
      </Card>
      <SaveChangesModal
        isOpen={showSaveChangesModal}
        onRequestClose={() => setShowSaveChangesModal(false)}
        savingWhat={`${auditTypeName}`}
        submitActions={() => history.goBack()}
      />
      <DeleteItemModal
        isOpen={showDeleteModal}
        onRequestClose={() => setShowDeleteModal(false)}
        deletingWhat={`${auditTypeName}`}
        submitActions={handleDelete}
      />
      <MissingRequiredModal
        isOpen={missingModalOpen}
        onRequestClose={() => setMissingModalOpen(false)}
        subject={auditTypeName}
      />
    </HeaderAndFooter>
  );
}
